import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type {
    DqStatRecord,
    GetNotRegisteredTablesParams,
    GetRegisteredDataSourcesParams,
    GetRegisteredDataSourcesResponse,
    NotRegisteredTablesResponse,
    RegisteredSourceFilesResponse,
} from '../services/source/source.types';
import { sourceService } from '../services/source/source.service';

export type TransformationLookupDatasourceSelection = {
    sourceDataSourceId: number;
    dataSourceName: string;
};

export const TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY = ['transformation-lookup-datasource-selection'] as const;

export const getTransformationLookupDatasourceSelectionQueryKey = (
    sourceDataSourceId: number | null | undefined
) => [
    ...TRANSFORMATION_LOOKUP_DATASOURCE_SELECTION_QUERY_KEY,
    sourceDataSourceId ?? 'none',
] as const;


interface UseRegisteredSourcesParams
    extends GetRegisteredDataSourcesParams {
    enabled?: boolean;
}

export const useRegisteredSources = (
    params?: UseRegisteredSourcesParams
) => {
    const {
        enabled = true,
        ...queryParams
    } = params || {};

    return useQuery<GetRegisteredDataSourcesResponse>({
        queryKey: ['registered-sources', queryParams],
        queryFn: () =>
            sourceService.getRegisteredDataSource(queryParams),
        enabled,
        staleTime: Infinity,
    });
};

export const useDqStatsBySourceFileQuery = (
    sourceFileId: number | undefined,
    sessionId: string | undefined,
    options?: Omit<UseQueryOptions<DqStatRecord[], Error>, 'queryKey' | 'queryFn'>,
) => {
    const resolvedSourceFileId = sourceFileId ?? 0;
    const resolvedSessionId = sessionId ?? '';
    return useQuery<DqStatRecord[], Error>({
        queryKey: ['adp', 'dq-stats', sourceFileId, sessionId],
        queryFn: () => sourceService.getDqStatsBySourceFile(resolvedSourceFileId, resolvedSessionId),
        enabled: Boolean(sourceFileId && sessionId),
        staleTime: 0,
        ...options,
    });
};

export const useRegisteredSourceFilesQuery = (
    sourceDataSourceId: number | null | undefined,
    tableName?: string,
    options?: Omit<UseQueryOptions<RegisteredSourceFilesResponse, Error>, 'queryKey' | 'queryFn'>,
) => {
    const trimmedTableName = tableName?.trim() || undefined;
    const resolvedSourceDataSourceId = sourceDataSourceId ?? 0;
    const hasValidSourceId = Number.isInteger(sourceDataSourceId) && Number(sourceDataSourceId) > 0;

    return useQuery<RegisteredSourceFilesResponse, Error>({
        queryKey: ['registered-source-files', sourceDataSourceId, trimmedTableName],
        queryFn: () => sourceService.listRegisteredSourceFiles(resolvedSourceDataSourceId, trimmedTableName),
        enabled: hasValidSourceId,
        staleTime: Infinity,
        ...options,
    });
};

export const useNotRegisteredTablesQuery = (
    params: Partial<GetNotRegisteredTablesParams> | null | undefined,
    options?: Omit<UseQueryOptions<NotRegisteredTablesResponse, Error>, 'queryKey' | 'queryFn'>,
) => {
    const sourceDataSourceId = params?.source_data_source_id ?? 0;
    const type = params?.type?.trim() ?? '';
    const isEnabled = sourceDataSourceId > 0 && type.length > 0;

    return useQuery<NotRegisteredTablesResponse, Error>({
        queryKey: ['not-registered-tables', sourceDataSourceId, type],
        queryFn: () => sourceService.getNotRegisteredTables({
            source_data_source_id: sourceDataSourceId,
            type,
        }),
        enabled: isEnabled,
        staleTime: 60 * 1000,
        ...options,
    });
};